/**
 * The ASCII art generator's fonts, drawn for CitationEasy.
 *
 * Original designs, not FIGlet fonts: each glyph is a list of equal-width rows, every
 * font covers A–Z, 0–9, the space and 26 punctuation marks, and lowercase input is
 * drawn with the capitals. Glyph keys are the characters they draw.
 *
 * - **Block** — 5 rows of `#`, the cell the fill character replaces. Also the bitmap
 *   the shadow style is computed from, so its glyphs use only `#` and spaces.
 * - **Slim** — 5 rows of line art. Row 0 holds top bars; a bar of `_` at the foot of
 *   row 2 is the middle stroke, so A, E, H, P and friends cross at mid-height.
 * - **Small** — 3 rows of line art in the same spirit, for a banner that fits a
 *   code comment.
 */

/** A font: each character's rows, top to bottom, all the same width. */
export type AsciiFont = Readonly<Record<string, readonly string[]>>;

export const BLOCK_GLYPHS: AsciiFont = {
  "0": [" ### ", "#  ##", "# # #", "##  #", " ### "],
  "1": [" # ", "## ", " # ", " # ", "###"],
  "2": [" ### ", "#   #", "  ## ", " #   ", "#####"],
  "3": ["#### ", "    #", " ### ", "    #", "#### "],
  "4": ["#   #", "#   #", "#####", "    #", "    #"],
  "5": ["#####", "#    ", "#### ", "    #", "#### "],
  "6": [" ### ", "#    ", "#### ", "#   #", " ### "],
  "7": ["#####", "    #", "   # ", "  #  ", "  #  "],
  "8": [" ### ", "#   #", " ### ", "#   #", " ### "],
  "9": [" ### ", "#   #", " ####", "    #", " ### "],
  A: [" ### ", "#   #", "#####", "#   #", "#   #"],
  B: ["#### ", "#   #", "#### ", "#   #", "#### "],
  C: [" ####", "#    ", "#    ", "#    ", " ####"],
  D: ["#### ", "#   #", "#   #", "#   #", "#### "],
  E: ["#####", "#    ", "#### ", "#    ", "#####"],
  F: ["#####", "#    ", "#### ", "#    ", "#    "],
  G: [" ####", "#    ", "#  ##", "#   #", " ####"],
  H: ["#   #", "#   #", "#####", "#   #", "#   #"],
  I: ["###", " # ", " # ", " # ", "###"],
  J: ["  ###", "    #", "    #", "#   #", " ### "],
  K: ["#   #", "#  # ", "###  ", "#  # ", "#   #"],
  L: ["#    ", "#    ", "#    ", "#    ", "#####"],
  M: ["#   #", "## ##", "# # #", "#   #", "#   #"],
  N: ["#   #", "##  #", "# # #", "#  ##", "#   #"],
  O: [" ### ", "#   #", "#   #", "#   #", " ### "],
  P: ["#### ", "#   #", "#### ", "#    ", "#    "],
  Q: [" ### ", "#   #", "# # #", "#  # ", " ## #"],
  R: ["#### ", "#   #", "#### ", "#  # ", "#   #"],
  S: [" ####", "#    ", " ### ", "    #", "#### "],
  T: ["#####", "  #  ", "  #  ", "  #  ", "  #  "],
  U: ["#   #", "#   #", "#   #", "#   #", " ### "],
  V: ["#   #", "#   #", "#   #", " # # ", "  #  "],
  W: ["#   #", "#   #", "# # #", "## ##", "#   #"],
  X: ["#   #", " # # ", "  #  ", " # # ", "#   #"],
  Y: ["#   #", " # # ", "  #  ", "  #  ", "  #  "],
  Z: ["#####", "   # ", "  #  ", " #   ", "#####"],
  " ": ["   ", "   ", "   ", "   ", "   "],
  ".": [" ", " ", " ", " ", "#"],
  ",": ["  ", "  ", "  ", " #", "# "],
  "!": ["#", "#", "#", " ", "#"],
  "?": [" ### ", "#   #", "  ## ", "     ", "  #  "],
  "'": ["#", "#", " ", " ", " "],
  '"': ["# #", "# #", "   ", "   ", "   "],
  "-": ["    ", "    ", "####", "    ", "    "],
  "+": ["   ", " # ", "###", " # ", "   "],
  "=": ["    ", "####", "    ", "####", "    "],
  ":": [" ", "#", " ", "#", " "],
  ";": ["  ", " #", "  ", " #", "# "],
  "(": [" #", "# ", "# ", "# ", " #"],
  ")": ["# ", " #", " #", " #", "# "],
  "[": ["##", "# ", "# ", "# ", "##"],
  "]": ["##", " #", " #", " #", "##"],
  "/": ["    #", "   # ", "  #  ", " #   ", "#    "],
  "\\": ["#    ", " #   ", "  #  ", "   # ", "    #"],
  "&": [" #   ", "# #  ", " #  #", "# ## ", " ## #"],
  "#": [" # # ", "#####", " # # ", "#####", " # # "],
  "@": [" ### ", "#   #", "# ###", "# # #", " ####"],
  "*": ["     ", "# # #", " ### ", "# # #", "     "],
  _: ["    ", "    ", "    ", "    ", "####"],
  "<": ["  #", " # ", "#  ", " # ", "  #"],
  ">": ["#  ", " # ", "  #", " # ", "#  "],
  $: [" ####", "# #  ", " ### ", "  # #", "#### "],
  "%": ["##  #", "## # ", "  #  ", " # ##", "#  ##"],
};

export const SLIM_GLYPHS: AsciiFont = {
  "0": [" ____ ", "|   /|", "|  / |", "| /  |", "|/___|"],
  "1": ["    ", " /| ", "  | ", "  | ", " _|_"],
  "2": [" ____ ", "     |", " ____|", "|     ", "|____ "],
  "3": [" ____ ", "     |", " ____|", "     |", " ____|"],
  "4": ["      ", "|    |", "|____|", "     |", "     |"],
  "5": [" ____ ", "|     ", "|____ ", "     |", " ____|"],
  "6": [" ____ ", "|     ", "|____ ", "|    |", "|____|"],
  "7": [" ____ ", "     /", "    / ", "   /  ", "  /   "],
  "8": [" ____ ", "|    |", "|____|", "|    |", "|____|"],
  "9": [" ____ ", "|    |", "|____|", "     |", " ____|"],
  A: [" ____ ", "|    |", "|____|", "|    |", "|    |"],
  B: [" ___  ", "|   \\ ", "|___/ ", "|   \\ ", "|___/ "],
  C: [" ____", "|    ", "|    ", "|    ", "|____"],
  D: [" ___  ", "|   \\ ", "|    |", "|    |", "|___/ "],
  E: [" ____", "|    ", "|___ ", "|    ", "|____"],
  F: [" ____", "|    ", "|___ ", "|    ", "|    "],
  G: [" ____ ", "|     ", "|  __ ", "|    |", "|____|"],
  H: ["      ", "|    |", "|____|", "|    |", "|    |"],
  I: ["___", " | ", " | ", " | ", "_|_"],
  J: ["  ___", "    |", "    |", "|   |", "|___|"],
  K: ["     ", "|   /", "|__/ ", "|  \\ ", "|   \\"],
  L: ["     ", "|    ", "|    ", "|    ", "|____"],
  M: ["      ", "|\\  /|", "| \\/ |", "|    |", "|    |"],
  N: ["      ", "|\\   |", "| \\  |", "|  \\ |", "|   \\|"],
  O: [" ____ ", "|    |", "|    |", "|    |", "|____|"],
  P: [" ____ ", "|    |", "|____|", "|     ", "|     "],
  Q: [" ____ ", "|    |", "|    |", "|    |", "|__\\_|"],
  R: [" ____ ", "|    |", "|____|", "|   \\ ", "|    \\"],
  S: [" ___ ", "/    ", "\\___ ", "    \\", " ___/"],
  T: ["_____", "  |  ", "  |  ", "  |  ", "  |  "],
  U: ["      ", "|    |", "|    |", "|    |", "|____|"],
  V: ["      ", "|    |", "|    |", " \\  / ", "  \\/  "],
  W: ["      ", "|    |", "|    |", "| /\\ |", "|/  \\|"],
  X: ["      ", "\\    /", " \\  / ", " /  \\ ", "/    \\"],
  Y: ["     ", "\\   /", " \\ / ", "  |  ", "  |  "],
  Z: [" ____", "    /", "   / ", "  /  ", " /___"],
  " ": ["   ", "   ", "   ", "   ", "   "],
  ".": [" ", " ", " ", " ", "o"],
  ",": ["  ", "  ", "  ", " o", "/ "],
  "!": [" ", "|", "|", " ", "o"],
  "?": [" ___ ", "    |", "  __|", "     ", "  o  "],
  "'": [" ", "|", " ", " ", " "],
  '"': ["   ", "| |", "   ", "   ", "   "],
  "-": ["    ", "    ", "____", "    ", "    "],
  "+": ["     ", "  |  ", "--+--", "  |  ", "     "],
  "=": ["    ", "____", "____", "    ", "    "],
  ":": [" ", " ", "o", " ", "o"],
  ";": ["  ", "  ", " o", " o", "/ "],
  "(": ["  ", " /", "| ", "| ", " \\"],
  ")": ["  ", "\\ ", " |", " |", "/ "],
  "[": [" _", "| ", "| ", "| ", "|_"],
  "]": ["_ ", " |", " |", " |", "_|"],
  "/": ["    ", "   /", "  / ", " /  ", "/   "],
  "\\": ["    ", "\\   ", " \\  ", "  \\ ", "   \\"],
  "&": ["  __  ", " /  \\ ", " \\__/ ", " /  \\/", " \\__/\\"],
  "#": ["       ", " _|_|_ ", " _|_|_ ", "  | |  ", "       "],
  "@": [" _____ ", "|  __ |", "| |  ||", "| |__/|", "|______"],
  "*": ["     ", "\\ | /", "-- --", "/ | \\", "     "],
  _: ["     ", "     ", "     ", "     ", "_____"],
  "<": ["   ", "  /", " / ", " \\ ", "  \\"],
  ">": ["   ", "\\  ", " \\ ", " / ", "/  "],
  $: [" _|_ ", "/ |  ", "\\_|_ ", "  | \\", " _|_/"],
  "%": ["o   /", "   / ", "  /  ", " /   ", "/   o"],
};

export const SMALL_GLYPHS: AsciiFont = {
  "0": [" _ ", "|/|", "|_|"],
  "1": ["  ", "/|", " |"],
  "2": [" _ ", " _|", "|_ "],
  "3": ["_ ", "_|", "_|"],
  "4": ["   ", "|_|", "  |"],
  "5": [" _ ", "|_ ", " _|"],
  "6": [" _ ", "|_ ", "|_|"],
  "7": ["__", " /", "/ "],
  "8": [" _ ", "|_|", "|_|"],
  "9": [" _ ", "|_|", " _|"],
  A: [" _ ", "|_|", "| |"],
  B: [" _ ", "|_)", "|_)"],
  C: [" _", "| ", "|_"],
  D: [" _ ", "| \\", "|_/"],
  E: [" _", "|_", "|_"],
  F: [" _", "|_", "| "],
  G: [" _ ", "| _", "|_|"],
  H: ["   ", "|_|", "| |"],
  I: [" ", "|", "|"],
  J: ["   ", "  |", "|_|"],
  K: ["  ", "|/", "|\\"],
  L: ["  ", "| ", "|_"],
  M: ["    ", "|\\/|", "|  |"],
  N: ["    ", "|\\ |", "| \\|"],
  O: [" _ ", "| |", "|_|"],
  P: [" _ ", "|_)", "|  "],
  Q: [" _ ", "| |", "|_\\"],
  R: [" _ ", "|_)", "| \\"],
  S: [" _ ", "(_ ", " _)"],
  T: ["___", " | ", " | "],
  U: ["   ", "| |", "|_|"],
  V: ["    ", "\\  /", " \\/ "],
  W: ["    ", "|  |", "|/\\|"],
  X: ["  ", "\\/", "/\\"],
  Y: ["   ", "\\_/", " | "],
  Z: ["__", " /", "/_"],
  " ": ["  ", "  ", "  "],
  ".": [" ", " ", "o"],
  ",": [" ", " ", "/"],
  "!": [" ", "|", "o"],
  "?": ["_ ", " )", " o"],
  "'": ["|", " ", " "],
  '"': ["||", "  ", "  "],
  "-": ["  ", "__", "  "],
  "+": ["   ", "_|_", " | "],
  "=": ["  ", "__", "__"],
  ":": [" ", "o", "o"],
  ";": [" ", "o", "/"],
  "(": [" /", "| ", " \\"],
  ")": ["\\ ", " |", "/ "],
  "[": [" _", "| ", "|_"],
  "]": ["_ ", " |", "_|"],
  "/": ["  /", " / ", "/  "],
  "\\": ["\\  ", " \\ ", "  \\"],
  "&": [" _  ", "(_)/", "(_)\\"],
  "#": ["     ", "_|_|_", "_|_|_"],
  "@": [" __ ", "| @|", "|__ "],
  "*": ["   ", "\\|/", "/|\\"],
  _: ["   ", "   ", "___"],
  "<": ["  ", " /", " \\"],
  ">": ["  ", "\\ ", "/ "],
  $: [" |_", "(| ", "_|)"],
  "%": ["o /", " / ", "/ o"],
};
